import { useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import { createCardViewList } from 'utils/formatting';
import { selectSecclGiaReadyMadeFundsAssetIds } from '../reducers/selectors';
const emptyFunds = [];
export const useGetFilteredFunds = (searchCategory, searchTerm, isInputFocused) => {
    const selectedTradingAccountId = useAppSelector((state) => state.unpersistedInvestReducer.selectedTradingAccountId);
    const secclAssets = useAppSelector((state) => state.unpersistedInvestReducer.secclAssets);
    const readyMadeFundsAssetIds = useAppSelector(selectSecclGiaReadyMadeFundsAssetIds);
    const recentlyViewedAssetIds = useAppSelector((state) => selectedTradingAccountId
        ? state.invest.recentlyViewedAssetIdsForTradingAccount?.[selectedTradingAccountId] || []
        : []);
    const searchResults = useMemo(() => {
        if (secclAssets) {
            if (searchTerm) {
                const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regexp = new RegExp(escaped, 'i');
                const data = secclAssets.filter((asset) => {
                    if (asset.symbol) {
                        return asset.name.match(regexp) || asset.symbol.match(regexp) || asset.originalName.match(regexp);
                    }
                    return asset.name.match(regexp) || asset.originalName.match(regexp);
                });
                const flashListData = createCardViewList(data);
                return flashListData;
            }
            if (isInputFocused && recentlyViewedAssetIds?.length) {
                return [
                    {
                        itemType: 'sectionTitle',
                        sectionTitle: 'Recently viewed',
                    },
                    {
                        itemType: 'recentlyViewedAssetIds',
                        recentlyViewedAssetIds,
                    },
                ];
            }
            const filteredFunds = (() => {
                if (searchCategory === 'Low risk') {
                    return secclAssets.filter((asset) => {
                        const fundRiskLevel = asset?.fundRiskLevel;
                        if (fundRiskLevel) {
                            if (fundRiskLevel <= 2) {
                                return true;
                            }
                        }
                        return false;
                    });
                }
                if (searchCategory === 'Medium risk') {
                    return secclAssets.filter((asset) => {
                        const fundRiskLevel = asset?.fundRiskLevel;
                        if (fundRiskLevel) {
                            if (fundRiskLevel >= 3 && fundRiskLevel <= 5) {
                                return true;
                            }
                        }
                        return false;
                    });
                }
                if (searchCategory === 'High risk') {
                    return secclAssets.filter((asset) => {
                        const fundRiskLevel = asset?.fundRiskLevel;
                        if (fundRiskLevel) {
                            if (fundRiskLevel >= 6) {
                                return true;
                            }
                        }
                        return false;
                    });
                }
                return secclAssets;
            })();
            const readyMadeList = [];
            const regionalList = [];
            const sectorialList = [];
            const readyMadeFundsAssetIdMap = readyMadeFundsAssetIds?.reduce((acc, curr) => {
                acc[curr] = true;
                return acc;
            }, {});
            filteredFunds.forEach((fund) => {
                if (readyMadeFundsAssetIdMap?.[fund.id]) {
                    readyMadeList.push(fund);
                }
                if (fund.fundGeo) {
                    regionalList.push(fund);
                }
                if (fund.fundTheme) {
                    sectorialList.push(fund);
                }
            });
            const finalArr = [];
            if (readyMadeList?.length) {
                finalArr.push({
                    itemType: 'sectionTitle',
                    sectionTitle: 'Ready-made',
                });
                finalArr.push(...createCardViewList(readyMadeList));
            }
            if (regionalList?.length) {
                finalArr.push({
                    itemType: 'sectionTitle',
                    sectionTitle: 'Regions',
                });
                finalArr.push(...createCardViewList(regionalList));
            }
            if (sectorialList?.length) {
                finalArr.push({
                    itemType: 'sectionTitle',
                    sectionTitle: 'Sectors',
                });
                finalArr.push(...createCardViewList(sectorialList));
            }
            return finalArr;
        }
        return emptyFunds;
    }, [isInputFocused, readyMadeFundsAssetIds, recentlyViewedAssetIds, searchCategory, searchTerm, secclAssets]);
    return {
        searchResults,
        hasLoadedFunds: !!secclAssets?.length,
        hasRecentlyViewedFunds: !!recentlyViewedAssetIds?.length,
    };
};
